import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IActivo, IQueryParam, IUsuario } from 'modelos/src';
import { Subscription } from 'rxjs';
import { ActivosService } from '../../../../auxiliares/servicios/http/activos.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';

export interface IAsignarConductorVehiculoData {
  vehiculo: IActivo;
  tipo: 'Conductor' | 'Chofer';
}

// Conductor con etiqueta precomputada (nombre + usuario) para el ng-select:
// permite buscar por ambos campos y ordenar alfabéticamente.
type IConductorOpcion = IUsuario & { label: string };

@Component({
  imports: [AuxiliaresModule],
  selector: 'app-asignar-conductor-vehiculo',
  templateUrl: './asignar-conductor-vehiculo.component.html',
  standalone: true,
})
export class AsignarConductorVehiculoComponent implements OnInit, OnDestroy {
  data = inject<IAsignarConductorVehiculoData>(MAT_DIALOG_DATA);
  private lista2 = inject(Lista2Service);
  private activosService = inject(ActivosService);
  private helper = inject(HelperService);
  private dialogRef =
    inject<MatDialogRef<AsignarConductorVehiculoComponent>>(MatDialogRef);

  public conductores: IConductorOpcion[] = [];
  public conductorSeleccionado?: IConductorOpcion;
  public conductores$?: Subscription;

  // Etiqueta visible/buscable: "Nombre completo (usuario)". Si no hay nombre
  // personal, cae al nombre de usuario.
  private armarLabel(u: IUsuario): string {
    const nombre = u.datosPersonales?.nombre?.trim();
    return nombre ? `${nombre} (${u.usuario})` : (u.usuario ?? '');
  }

  // Solo conductores del propio cliente del vehículo. includeChildren:true para
  // que el scope alcance clientes hijos (si el vehículo es de un cliente hijo),
  // pero se filtra por el idCliente exacto del vehículo (query + client-side).
  public query: IQueryParam = {
    page: 0,
    includeChildren: true,
    childrenLevel: 0,
    sort: 'usuario',
  };

  public close(result?: boolean): void {
    this.dialogRef.close(result);
  }

  public async asignar(): Promise<void> {
    if (!this.conductorSeleccionado?._id) {
      this.helper.notifError('Seleccioná un conductor');
      return;
    }
    try {
      await this.activosService.asignarChofer(
        this.data.vehiculo._id!,
        this.conductorSeleccionado._id,
      );
      this.helper.notifSuccess('Conductor asignado');
      this.close(true);
    } catch (error) {
      console.error(error);
      this.helper.notifError('Error al asignar');
    }
  }

  public async desasignar(): Promise<void> {
    try {
      await this.activosService.update(this.data.vehiculo._id!, {
        vehiculo: { ...this.data.vehiculo.vehiculo, idChofer: null },
      });
      this.helper.notifSuccess('Conductor desasignado');
      this.close(true);
    } catch (error) {
      console.error(error);
      this.helper.notifError('Error al desasignar');
    }
  }

  private listar(): void {
    this.conductores$?.unsubscribe();
    const idClienteVehiculo = this.data.vehiculo?.idCliente;
    this.query.filter = JSON.stringify(
      idClienteVehiculo ? { idCliente: idClienteVehiculo } : {},
    );
    const obs =
      this.data.tipo === 'Chofer'
        ? this.lista2.chofers(this.query)
        : this.lista2.conductors(this.query);

    this.conductores$ = obs.subscribe((res) => {
      this.conductores = (res.datos || [])
        .filter(
          (u) =>
            !idClienteVehiculo ||
            String(u.idCliente) === String(idClienteVehiculo),
        )
        .map((u) => ({ ...u, label: this.armarLabel(u) }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
        );
      if (this.data.vehiculo.vehiculo?.idChofer) {
        this.conductorSeleccionado = this.conductores.find(
          (c) => c._id === this.data.vehiculo.vehiculo!.idChofer,
        );
      }
    });
  }

  ngOnInit(): void {
    this.listar();
  }

  ngOnDestroy(): void {
    this.conductores$?.unsubscribe();
  }
}
